import type { EmploymentStatus } from '@/types/hrInterview'

/**
 * Catalogo y formato de los datos del candidato (B2).
 *
 * Viven fuera del componente porque exportar constantes y funciones desde un
 * archivo de componente rompe el fast refresh, y ese aviso cuenta contra el
 * tope del lint.
 */

export const ESTATUS_LABORAL: { value: EmploymentStatus; label: string }[] = [
  { value: 'employed', label: 'Empleado' },
  { value: 'self_employed', label: 'Cuenta propia' },
  { value: 'unemployed', label: 'Desempleado' },
]

export const etiquetaDeEstatus = (valor?: EmploymentStatus | null) =>
  ESTATUS_LABORAL.find((e) => e.value === valor)?.label ?? 'No especificado'

/**
 * «$18,500.00», o un guion si no se capturo.
 *
 * Acepta cadena porque el servidor manda los decimal asi.
 */
export const formatoDeSueldo = (valor?: number | string | null): string => {
  if (valor === null || valor === undefined || Number.isNaN(Number(valor))) return '—'
  return `$${Number(valor).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * El importe del servidor, como numero, para el formulario.
 *
 * Los decimal llegan como cadena; si se metieran tal cual al estado del form,
 * al guardar se mandaria texto donde el DTO espera numero.
 */
export const aImporte = (valor?: number | string | null): number | null => {
  if (valor === null || valor === undefined || valor === '') return null
  const n = Number(valor)
  return Number.isNaN(n) ? null : n
}

/**
 * Con qué llega el formulario de resultados cuando se abre.
 *
 * Lo que el candidato declaró al postularse —su expectativa de sueldo y su
 * ciudad— se trae ya puesto, en vez de pedírselo otra vez a quien entrevista:
 * el dato ya existe, y volver a capturarlo a mano es por donde se cuelan las
 * diferencias entre la ficha y la entrevista.
 *
 * Queda editable: en la entrevista es normal que la expectativa cambie, y lo
 * que se guarda aquí es lo que se habló, no lo que se escribió en el
 * formulario público.
 *
 * Lo ya capturado en la entrevista manda sobre lo declarado; el dato del
 * candidato solo rellena el hueco.
 */
export function conLoDeclaradoPorElCandidato<
  T extends { salaryExpectation?: number | null; residenceCity?: string },
>(
  resultados: T,
  /**
   * Los decimal del back llegan como cadena, así que el sueldo entra tal cual
   * y lo normaliza `aImporte`.
   */
  candidato: {
    salaryExpectation?: number | string | null
    residenceCity?: string | null
  } | null,
): T {
  if (!candidato) return resultados

  const sinSueldo =
    resultados.salaryExpectation === null || resultados.salaryExpectation === undefined
  const sinCiudad = (resultados.residenceCity ?? '').trim() === ''

  return {
    ...resultados,
    salaryExpectation: sinSueldo
      ? aImporte(candidato.salaryExpectation)
      : resultados.salaryExpectation,
    residenceCity: sinCiudad
      ? (candidato.residenceCity ?? '').trim()
      : resultados.residenceCity,
  }
}
